import React from 'react';
import {
  MdIosShare,
  MdList,
  MdSettings,
  MdSupervisedUserCircle,
  MdInsertChart,
  MdOutlineAssignment
} from 'react-icons/md';
import { NextRouter, useRouter } from 'next/router';
import { RiCalendarCheckFill } from 'react-icons/ri';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';

import useViewAssociation from '../../hooks/useViewAssociation';
import JoinButton from '../../../../components/view-org-layout/JoinButton';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

type MoreOptionsProps = {
  toggleShareModal: () => void;
  toggleMoreOptions: () => void;
  visible: boolean;
};

type MoreOption = {
  id: string;
  condition: (props: {
    isUserAdmin?: boolean;
    showWorkflows?: boolean;
    hasWorkflowId?: boolean;
  }) => boolean;
  onClick?: (props: {
    associationId: string;
    router: NextRouter;
  }) => () => void;
  label: string | React.ReactNode;
  icon: React.ReactNode;
};

const MORE_OPTIONS: MoreOption[] = [
  {
    id: 'association-analytics',
    condition: ({ isUserAdmin }) => isUserAdmin,
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/analytics`);
      },
    label: 'associationAnalytics',
    icon: <MdInsertChart size={20} />
  },
  {
    condition: ({ isUserAdmin }) => isUserAdmin,
    id: 'association-members',
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/members`);
      },
    label: 'associationMembers',
    icon: <MdSupervisedUserCircle size={24} />
  },
  {
    condition: ({ isUserAdmin, hasWorkflowId }) =>
      isUserAdmin && !hasWorkflowId,
    id: 'association-events-requests',
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/events/requests`);
      },
    label: 'associationEventsRequests',
    icon: <RiCalendarCheckFill size={24} />
  },
  {
    condition: ({ isUserAdmin }) => isUserAdmin,
    id: 'association-events-log',
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/events/log`);
      },
    label: 'associationEventsLog',
    icon: <MdList size={24} />
  },
  {
    condition: ({ showWorkflows }) => showWorkflows,
    id: 'association-workflows',
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/workflows`);
      },
    label: 'associationWorkflows',
    icon: <MdOutlineAssignment size={24} />
  },
  {
    condition: ({ isUserAdmin }) => isUserAdmin,
    id: 'edit-association',
    onClick:
      ({ associationId, router }) =>
      () => {
        router.push(`/associations/${associationId}/edit`);
      },
    label: 'associationSettings',
    icon: <MdSettings size={24} />
  }
];

/**
 * TODO: Make this reusable as there's a bit too much of duplication with this component.
 * Implementation here is almost the right one.
 * Only piece missing is to let the content of it being set from the consumer instead of here.
 */
const MoreOptionsModal: React.FC<MoreOptionsProps> = ({
  toggleShareModal,
  toggleMoreOptions,
  visible
}) => {
  const router = useRouter();
  const flags = useFlags();
  const { t } = useTranslation(['associations']);
  const {
    data: { association },
    joinAssociation
  } = useViewAssociation();

  const onOptionClick = (action) => () => {
    toggleMoreOptions();
    action();
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleMoreOptions}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {MORE_OPTIONS.map((option) =>
            option.condition({
              isUserAdmin: association.isUserAdmin,
              showWorkflows: flags[FLAGS.ENABLE_ASSOCIATION_WORKFLOWS],
              hasWorkflowId: Boolean(association.requestToPublishWorkflowId)
            }) ? (
              <li
                key={option.id}
                className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                onClick={onOptionClick(
                  option.onClick({
                    associationId: association.id,
                    router
                  })
                )}
              >
                {option.icon}
                {t(`moreOptions.${option.label}`)}
              </li>
            ) : null
          )}
        </ul>
        {!association.isUserAdmin && (
          <>
            <div className="flex gap-2 flex-col pb-2">
              <Button
                type={ButtonType.Inverted}
                onClick={onOptionClick(toggleShareModal)}
                icon={<MdIosShare size={20} />}
              >
                {t('shareAssociation')}
              </Button>
            </div>
            <Divider size={4} />
          </>
        )}
        <div className="flex flex-col gap-2 mt-6">
          {association.isUserAdmin ? (
            <Button
              type={ButtonType.Inverted}
              onClick={onOptionClick(toggleShareModal)}
              icon={<MdIosShare size={20} />}
            >
              {t('shareAssociation')}
            </Button>
          ) : (
            <JoinButton
              isUserPending={association.isUserPending}
              isUserMember={association.isUserMember}
              isUserAdmin={association.isUserAdmin}
              join={joinAssociation}
              isAssociation
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MoreOptionsModal;
